import React, { useState, useEffect, useRef } from 'react';
import { MethodBadge } from './utils/colorConfig';

function TipHistoryTable({ tips }) {
  const [selectedTip, setSelectedTip] = useState(null);
  const modalRef = useRef(null);
  const triggerRef = useRef(null);

  // Format date to a readable format
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    };
    return new Intl.DateTimeFormat('en-GB', options).format(date);
  };

  // Format amount based on currency
  const formatAmount = (amount, currency) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 2
    }).format(amount / 100); // Assuming amount is in cents
  };

  const handleRowClick = (tip, event) => {
    setSelectedTip(tip);
    triggerRef.current = event.currentTarget;
  };

  const closeModal = () => {
    setSelectedTip(null);
  };

  const handleKeyDown = (event, tip) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleRowClick(tip, event);
    }
  };

  useEffect(() => {
    if (selectedTip) {
      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      firstElement?.focus();

      const handleTabKeyPress = (e) => {
        if (e.key === 'Tab') {
          if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          } else if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      };

      const handleEscapeKeyPress = (e) => {
        if (e.key === 'Escape') {
          closeModal();
        }
      };

      modalRef.current.addEventListener('keydown', handleTabKeyPress);
      document.addEventListener('keydown', handleEscapeKeyPress);

      return () => {
        modalRef.current?.removeEventListener('keydown', handleTabKeyPress);
        document.removeEventListener('keydown', handleEscapeKeyPress);
        triggerRef.current?.focus();
      };
    }
  }, [selectedTip]);

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border border-gray-200">
          <thead>
            <tr className="bg-gray-100">
              <th scope="col" className="py-2 px-4 border-b text-left">Date</th>
              <th scope="col" className="py-2 px-4 border-b text-left hidden md:table-cell">Source</th>
              <th scope="col" className="py-2 px-4 border-b text-left">Name</th>
              <th scope="col" className="py-2 px-4 border-b text-left hidden md:table-cell">Message</th>
              <th scope="col" className="py-2 px-4 border-b text-right">Amount</th>
              <th scope="col" className="py-2 px-4 border-b text-center hidden md:table-cell">Payment method</th>
            </tr>
          </thead>
          <tbody>
            {tips.map((tip) => (
              <tr 
                key={tip.id} 
                className="hover:bg-gray-50 cursor-pointer"
                onClick={(e) => handleRowClick(tip, e)}
                onKeyDown={(e) => handleKeyDown(e, tip)}
                tabIndex="0"
                role="button"
                aria-label={`View details for tip from ${tip.name || 'Anonymous'} on ${formatDate(tip.timestamp)}`}
              >
                <td className="py-2 px-4 border-b">
                  {formatDate(tip.timestamp)}
                </td>
                <td className="py-2 px-4 border-b hidden md:table-cell">
                  <div className="truncate max-w-xs" title={tip.url}>
                    {tip.url}
                  </div>
                </td>
                <td className="py-2 px-4 border-b">
                  <div className="truncate max-w-xs" title={tip.name}>
                    {tip.name || 'Anonymous'}
                  </div>
                </td>
                <td className="py-2 px-4 border-b hidden md:table-cell">
                  <div className="truncate max-w-xs" title={tip.message}>
                    {tip.message || '-'}
                  </div>
                </td>
                <td className="py-2 px-4 border-b text-right">
                  {formatAmount(tip.amount, tip.currency)}
                </td>
                <td className="py-2 px-4 border-b text-center hidden md:table-cell">
                  <MethodBadge method={tip.method} />
                </td>
              </tr>
            ))}

            {tips.length === 0 && (
              <tr>
                <td colSpan="6" className="py-8 text-center text-gray-500">
                  No tip history available yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {tips.length > 0 && (
        <div className="mt-4 text-sm text-gray-500 text-center">
          Showing all {tips.length} tips
        </div>
      )}
      {selectedTip && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center p-4 z-50"
          onClick={closeModal}
        >
          <div 
            ref={modalRef} 
            className="bg-white rounded-lg shadow-xl w-auto max-w-md" 
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
          >
            <div className="p-6">
              <div className="flex justify-between items-start">
                <h3 id="modal-title" className="text-xl font-semibold text-gray-800">Tip details</h3>
                <button onClick={closeModal} className="text-gray-400 hover:text-gray-600 text-2xl" aria-label="Close modal">&times;</button>
              </div>
              <div className="mt-4 space-y-3">
                <p><strong className="font-medium text-gray-600 w-20 inline-block">Date:</strong> {formatDate(selectedTip.timestamp)}</p>
                <p><strong className="font-medium text-gray-600 w-20 inline-block">Name:</strong> {selectedTip.name || 'Anonymous'}</p>
                <p><strong className="font-medium text-gray-600 w-20 inline-block">Amount:</strong> {formatAmount(selectedTip.amount, selectedTip.currency)}</p>
                <p><strong className="font-medium text-gray-600 w-20 inline-block">Source:</strong> <span className="break-all">{selectedTip.url || '-'}</span></p>
                <p><strong className="font-medium text-gray-600 w-20 inline-block">Message:</strong> {selectedTip.message || '-'}</p>
                <div className="flex items-center"><strong className="font-medium text-gray-600 w-20 inline-block">Method:</strong> <MethodBadge method={selectedTip.method} /></div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TipHistoryTable;
